/**
 * Custom Web Component for bidirectional horizontal scrolling announcement
 * Automatically scrolls right until the end, then scrolls left until the beginning,
 * and repeats this pattern continuously.
 */

if (!customElements.get('scroll-announcement')) {
  customElements.define('scroll-announcement', class ScrollAnnouncement extends HTMLElement {
    constructor() {
      super();

      this.track = this.querySelector('.announcement-track');
      this.speed = parseInt(this.getAttribute('data-speed')) || 50; // pixels per second
      this.currentPosition = 0;
      this.direction = 1; // 1 for right, -1 for left
      this.animationFrame = null;
      this.lastTimestamp = null;
      this.maxScroll = 0;
      this.isInitialized = false;

      this.observeInView();
    }

    disconnectedCallback() {
      this.stopScrolling();
    }

    /**
     * Initialize the scrolling behavior
     */
    init() {
      if (this.isInitialized) return;

      // Clone the content to ensure seamless scrolling
      this.cloneContent();

      // Calculate the maximum scroll distance
      this.calculateMaxScroll();

      // Start scrolling
      this.startScrolling();

      this.isInitialized = true;

      // Handle window resize
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      this.resizeObserver.observe(this);
    }

    /**
     * Clone the track content to ensure there's always content to scroll
     */
    cloneContent() {
      const originalContent = this.track.innerHTML;
      // Duplicate content multiple times to ensure smooth scrolling
      this.track.innerHTML = originalContent + originalContent + originalContent;
    }

    /**
     * Calculate the maximum scroll distance
     */
    calculateMaxScroll() {
      const trackWidth = this.track.scrollWidth / 3; // Divide by 3 since we cloned it 3 times
      const containerWidth = this.offsetWidth;
      this.maxScroll = trackWidth;
    }

    /**
     * Start the scrolling animation
     */
    startScrolling() {
      const animate = (timestamp) => {
        if (!this.lastTimestamp) {
          this.lastTimestamp = timestamp;
        }

        const deltaTime = (timestamp - this.lastTimestamp) / 1000; // Convert to seconds
        this.lastTimestamp = timestamp;

        // Calculate movement based on speed and time
        const movement = this.speed * deltaTime;
        this.currentPosition += movement * this.direction;

        // Check boundaries and reverse direction
        if (this.direction === 1 && this.currentPosition >= this.maxScroll) {
          this.currentPosition = this.maxScroll;
          this.direction = -1;
        } else if (this.direction === -1 && this.currentPosition <= 0) {
          this.currentPosition = 0;
          this.direction = 1;
        }

        // Apply the transform
        this.track.style.transform = `translateX(-${this.currentPosition}px)`;

        // Continue animation
        this.animationFrame = requestAnimationFrame(animate);
      };

      this.animationFrame = requestAnimationFrame(animate);
    }

    /**
     * Stop the scrolling animation
     */
    stopScrolling() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    /**
     * Handle window resize
     */
    handleResize() {
      this.calculateMaxScroll();
      // Reset position if it exceeds new bounds
      if (this.currentPosition > this.maxScroll) {
        this.currentPosition = this.maxScroll;
      }
    }

    /**
     * Observe when element enters viewport
     */
    observeInView() {
      const observer = new IntersectionObserver((entries, observer) => {
        if (entries[0].isIntersecting) {
          this.init();
          observer.disconnect();
        }
      }, {
        root: null,
        rootMargin: "100px 0px 100px 0px"
      });

      observer.observe(this);
    }
  });
}
